import { AlertMessage } from "./alerts";
// Primitivos de interface reaproveitados pelas telas de equipe e de admin.

import type { ContestStatus, SubmissionStatus, Verdict } from "@compila/shared";
import {
  CONTEST_STATUS_LABEL,
  VERDICT_LABEL,
  formatClock,
  verdictClasses,
} from "../lib/format.js";

export function VerdictBadge({
  status,
  verdict,
  size = "normal",
}: {
  status: SubmissionStatus;
  verdict: Verdict | null;
  size?: "normal" | "big";
}) {
  const pad = size === "big" ? "px-3 py-1 text-base" : "px-2 py-0.5 text-xs";
  const base = `inline-flex items-center rounded font-semibold ring-1 ring-inset ${pad}`;

  if (status === "QUEUED" || status === "JUDGING") {
    return (
      <span
        className={`${base} animate-pulse bg-amber-100 text-amber-800 ring-amber-600/30`}
      >
        {status === "QUEUED" ? "Na fila" : "Julgando"}
      </span>
    );
  }

  if (status === "INTERNAL_ERROR") {
    // Falha do juiz, nao da equipe: nao penaliza e sera rejulgada.
    return (
      <span
        className={`${base} bg-violet-100 text-violet-800 ring-violet-600/30`}
        title="Falha do sistema de julgamento. Nao conta como tentativa errada; o admin vai rejulgar."
      >
        Falha do juiz
      </span>
    );
  }

  if (!verdict) return <span className={`${base} bg-slate-100 text-slate-600`}>—</span>;

  return (
    <span className={`${base} ${verdictClasses(verdict)}`} title={VERDICT_LABEL[verdict]}>
      {verdict}
    </span>
  );
}

export function ContestStatusBadge({ status }: { status: ContestStatus }) {
  const cores: Record<ContestStatus, string> = {
    DRAFT: "bg-slate-200 text-slate-700",
    RUNNING: "bg-emerald-100 text-emerald-800",
    PAUSED: "bg-amber-100 text-amber-800",
    ENDED: "bg-slate-800 text-white",
  };
  return (
    <span className={`rounded px-2 py-1 text-xs font-semibold ${cores[status]}`}>
      {CONTEST_STATUS_LABEL[status]}
    </span>
  );
}

export function Countdown({
  elapsed,
  duration,
  status,
  size = "normal",
}: {
  elapsed: number;
  duration: number;
  status: ContestStatus;
  size?: "normal" | "big";
}) {
  const remaining = Math.max(0, duration - elapsed);
  // Ultimos 5 minutos em vermelho — o sinal que a equipe precisa ver de longe.
  const urgent = status === "RUNNING" && remaining <= 300;

  return (
    <span
      className={[
        "font-mono font-bold tabular-nums",
        size === "big" ? "text-5xl" : "text-xl",
        urgent ? "text-rose-600" : "text-slate-900",
        status === "PAUSED" ? "opacity-50" : "",
      ].join(" ")}
      title="Tempo restante de prova"
    >
      {formatClock(remaining)}
    </span>
  );
}

export function FrozenBanner({ big = false }: { big?: boolean }) {
  return (
    <div
      className={`flex items-center justify-center gap-2 rounded bg-sky-600 font-bold text-white ${
        big ? "px-6 py-3 text-2xl" : "px-3 py-1.5 text-sm"
      }`}
    >
      <span aria-hidden>❄</span>
      PLACAR CONGELADO
    </div>
  );
}

export function Spinner({ label = "Carregando..." }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
      {label}
    </div>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return <AlertMessage tone={"error"}>{message}</AlertMessage>;
}

export function InfoBox({
  children,
  tone = "info",
}: {
  children: React.ReactNode;
  tone?: "info" | "warning";
}) {
  return <AlertMessage tone={tone}>{children}</AlertMessage>;
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger";
}) {
  const variantes = {
    primary: "bg-sky-600 text-white hover:bg-sky-700 disabled:bg-slate-300",
    secondary:
      "bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:text-slate-400",
    danger: "bg-rose-600 text-white hover:bg-rose-700 disabled:bg-slate-300",
  };
  return (
    <button
      {...props}
      className={`rounded px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${variantes[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export const inputClasses =
  "w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

export function ConnectionDot({ state }: { state: "connecting" | "open" | "closed" }) {
  const cores = {
    open: "bg-emerald-500",
    connecting: "bg-amber-400 animate-pulse",
    closed: "bg-slate-400",
  };
  const titulo = {
    open: "Conectado — atualizacoes em tempo real",
    connecting: "Reconectando...",
    closed: "Desconectado",
  };
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${cores[state]}`}
      title={titulo[state]}
      aria-label={titulo[state]}
    />
  );
}
